import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setAuthFailureHandler, setJwt } from '../services/httpService';
import { getAuthBootstrap, logoutUser, refreshAuthSession } from '../services/authService';

type UserType = {
    id: string;
    _id?: string;
    fullName?: string;
    gymName: string;
    email: string;
    address: string;
    mobile: string;
    role?: string | {
        _id?: string;
        id?: string;
        name?: string;
        permissions?: string[];
    };
};

type AuthContextType = {
    token: string | null;
    user: UserType | null;
    ready: boolean;
    login: (token: string, user: UserType) => Promise<void>;
    logout: () => Promise<void>;
};

const USER_KEY = 'user';
const AUTH_EVENT_KEY = 'auth_event';

const AuthContext = createContext<AuthContextType | null>(null);

const readStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === 'string');
};

const readRole = (value: unknown): UserType['role'] => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
        const roleObj = value as { _id?: string; id?: string; name?: string; label?: string; role?: string; permissions?: unknown };
        return {
            _id: roleObj._id,
            id: roleObj.id,
            name: roleObj.name ?? roleObj.label ?? roleObj.role,
            permissions: readStringArray(roleObj.permissions),
        };
    }
    return undefined;
};

const normalizeUser = (value: unknown): UserType | null => {
    if (!value || typeof value !== 'object') return null;
    const userObj = value as Record<string, unknown>;
    const id = typeof userObj.id === 'string'
        ? userObj.id
        : typeof userObj._id === 'string'
            ? userObj._id
            : '';
    const email = typeof userObj.email === 'string' ? userObj.email : '';
    if (!id && !email) return null;

    return {
        id,
        _id: typeof userObj._id === 'string' ? userObj._id : undefined,
        fullName: typeof userObj.fullName === 'string' ? userObj.fullName : '',
        gymName: typeof userObj.gymName === 'string' ? userObj.gymName : '',
        email,
        address: typeof userObj.address === 'string' ? userObj.address : '',
        mobile: typeof userObj.mobile === 'string' ? userObj.mobile : '',
        role: readRole(userObj.role),
    };
};

const extractUser = (value: unknown): UserType | null => {
    if (!value || typeof value !== 'object') return null;
    const data = value as Record<string, unknown>;
    const nestedData = data.data && typeof data.data === 'object' ? data.data as Record<string, unknown> : null;
    const source = nestedData ?? data;
    return normalizeUser(source.user ?? source.currentUser ?? source.profile ?? source);
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<UserType | null>(null);
    const [ready, setReady] = useState(false);

    const clearAuth = useCallback(() => {
        setJwt(null);
        setToken(null);
        setUser(null);
        try {
            localStorage.removeItem('token');
            localStorage.removeItem(USER_KEY);
        } catch {/* ignore */}
    }, []);

    const applyAuth = useCallback((jwt: string, userData: unknown) => {
        const normalizedUser = normalizeUser(userData);
        setJwt(jwt);
        setToken(jwt);
        setUser(normalizedUser);
        try {
            localStorage.removeItem('token');
            if (normalizedUser) localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
            else localStorage.removeItem(USER_KEY);
        } catch {/* ignore */}
    }, []);

    const broadcastLogout = useCallback(() => {
        try {
            localStorage.setItem(AUTH_EVENT_KEY, JSON.stringify({ type: 'logout', at: Date.now() }));
        } catch {/* ignore */}
    }, []);

    useEffect(() => {
        const hydrate = async () => {
            try {
                localStorage.removeItem('token');
                const refreshed = await refreshAuthSession();
                applyAuth(refreshed.token, refreshed.user);
            } catch {
                clearAuth();
            }
        };

        hydrate()
            .catch((error) => console.warn('Auth hydrate failed', error))
            .finally(() => setReady(true));
    }, [applyAuth, clearAuth]);

    useEffect(() => {
        setJwt(token);
    }, [token]);

    useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key !== AUTH_EVENT_KEY || !event.newValue) return;
            try {
                const authEvent = JSON.parse(event.newValue) as { type?: string };
                if (authEvent.type === 'logout') clearAuth();
            } catch {/* ignore malformed cross-tab event */}
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [clearAuth]);

    useEffect(() => {
        setAuthFailureHandler(() => {
            clearAuth();
            broadcastLogout();
        });
        return () => setAuthFailureHandler(null);
    }, [broadcastLogout, clearAuth]);

    const login = async (jwt: string, userData: UserType) => {
        const normalizedUser = normalizeUser(userData);
        applyAuth(jwt, normalizedUser);

        if (!normalizedUser) {
            try {
                const bootstrap = await getAuthBootstrap();
                const bootstrapUser = extractUser(bootstrap);
                if (bootstrapUser) {
                    setUser(bootstrapUser);
                    localStorage.setItem(USER_KEY, JSON.stringify(bootstrapUser));
                }
            } catch (error) {
                console.warn('Auth bootstrap after login failed', error);
            }
        }
    };

    const logout = async () => {
        try {
            await logoutUser();
        } catch (error) {
            console.warn('Server logout failed', error);
        } finally {
            clearAuth();
            broadcastLogout();
        }
    };

    const value = useMemo(
        () => ({ token, user, ready, login, logout }),
        [token, user, ready, clearAuth, broadcastLogout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
    return ctx;
};
